/**
 * Plays muted background videos only while they are near the viewport.
 *
 * A `<video autoplay>` starts downloading and decoding as soon as it is parsed,
 * even when it sits in a hidden slide or three screens down. Videos rendered
 * with `preload="none"`, no `autoplay`, and a `data-lazy-autoplay` attribute
 * are picked up here instead: they start when scrolled near and pause again
 * when they leave, so offscreen media costs nothing. Visitors who ask for
 * reduced motion keep the poster frame.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const visibleVideos = new Set();
const observedVideos = new Set();

const updatePlayback = () => {
  for (const video of visibleVideos) {
    if (
      reducedMotion.matches ||
      document.hidden ||
      !video.isConnected ||
      video.closest('[hidden], [inert], [aria-hidden="true"]')
    ) {
      video.pause();
    } else if (video.paused) {
      const deferredSources = video.querySelectorAll('source[data-lazy-src]');
      for (const source of deferredSources) {
        source.src = source.dataset.lazySrc;
        source.removeAttribute('data-lazy-src');
      }
      if (deferredSources.length) video.load();
      video.play().catch(() => {});
    }
  }
};

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const video = entry.target;
      if (!(video instanceof HTMLVideoElement)) continue;

      if (!entry.isIntersecting || entry.intersectionRatio === 0) {
        visibleVideos.delete(video);
        video.pause();
        continue;
      }

      visibleVideos.add(video);
    }
    updatePlayback();
  },
  { rootMargin: '200px 0px', threshold: [0, 0.01] }
);

const slideObserver = new MutationObserver(updatePlayback);

/** @param {ParentNode} root */
const observe = (root) => {
  for (const video of root.querySelectorAll('video[data-lazy-autoplay]')) {
    observedVideos.add(video);
    observer.observe(video);
    const slide = video.closest('slideshow-slide');
    if (slide) {
      slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden', 'hidden', 'inert'] });
    }
  }
};

observe(document);

document.addEventListener('visibilitychange', updatePlayback);
reducedMotion.addEventListener('change', updatePlayback);

// Sections re-rendered by the theme editor arrive with fresh video elements
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) observe(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;

  for (const video of event.target.querySelectorAll('video[data-lazy-autoplay]')) {
    if (!(video instanceof HTMLVideoElement)) continue;
    observer.unobserve(video);
    visibleVideos.delete(video);
    observedVideos.delete(video);
    video.pause();
  }

  slideObserver.disconnect();
  for (const video of observedVideos) {
    const slide = video.closest('slideshow-slide');
    if (slide) {
      slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden', 'hidden', 'inert'] });
    }
  }
});
